import type { HTMLAttributeAnchorTarget } from 'react';
import { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router';

import { getHelpUrl } from '@change-corgi/config/help';
import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import { useLocale } from 'src/app/shared/hooks/l10n';

type UseErrorScreenReturn = ModelHookResult<
	{
		heading: TranslationKey;
		description: TranslationKey;
		primaryActionLabel: TranslationKey;
		primaryActionHref: string;
		primaryActionTarget?: HTMLAttributeAnchorTarget;
	},
	{ handlePrimaryActionClick: () => void }
>;

const UNAUTHENTICATED_ERROR_CODE = 'sap_api_session_timeout';

export function useErrorScreen(error: Error): UseErrorScreenReturn {
	const locale = useLocale();
	const location = useLocation();

	const isUnauthorizedError = error.message.includes(UNAUTHENTICATED_ERROR_CODE);

	const errorKeys = useMemo(() => {
		if (isUnauthorizedError)
			return {
				heading: translationKey('corgi.start_a_petition.prepublish.error.unauthorized.heading'),
				description: translationKey('corgi.start_a_petition.prepublish.error.unauthorized.description'),
				button: translationKey('corgi.start_a_petition.prepublish.error.unauthorized.button'),
			};
		return {
			heading: translationKey('corgi.start_a_petition.prepublish.error.unrecoverable.heading'),
			description: translationKey('corgi.start_a_petition.prepublish.error.unrecoverable.description'),
			button: translationKey('corgi.start_a_petition.prepublish.error.unrecoverable.button'),
		};
	}, [isUnauthorizedError]);

	const redirectUrl = encodeURIComponent(location.pathname + location.search);
	const helpUrl = getHelpUrl(locale, 'contact-us');

	const primaryActionHref = isUnauthorizedError ? `/login_or_join?redirectTo=${redirectUrl}&user_flow=sap` : helpUrl;

	const primaryActionTarget = isUnauthorizedError ? '_self' : '_blank';

	const handlePrimaryActionClick = useCallback(() => {
		// todo: track event
	}, []);

	return {
		data: {
			heading: errorKeys.heading,
			description: errorKeys.description,
			primaryActionLabel: errorKeys.button,
			primaryActionHref,
			primaryActionTarget,
		},
		actions: {
			handlePrimaryActionClick,
		},
	};
}
